'use client';

import React, { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Testimonial {
  id: number;
  name: string;
  role: string;
  company: string;
  content: string;
  rating: number;
  image: string;
  alt: string;
}

interface Metric {
  id: number;
  value: string;
  label: string;
  icon: string;
  suffix?: string;
}

interface SocialProofProps {
  className?: string;
}

const SocialProof = ({ className = '' }: SocialProofProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isHydrated]);

  const testimonials: Testimonial[] = [
  {
    id: 1,
    name: 'Sophie van der Berg',
    role: 'CEO',
    company: 'RetailPro Nederland',
    content: 'AIM Agency heeft onze klantenservice getransformeerd. De AI-chatbot bespaart ons €15.000 per maand en onze klanten zijn tevredener dan ooit. De implementatie was soepel en het team denkt echt mee.',
    rating: 5,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e95358c6-1772081363912.png",
    alt: 'Professional woman with blonde hair in navy blazer smiling confidently in modern office'
  },
  {
    id: 2,
    name: 'Mark Janssen',
    role: 'Operations Director',
    company: 'LogisticsFirst B.V.',
    content: 'De procesautomatisering heeft onze efficiëntie met 60% verhoogd. We kunnen nu meer orders verwerken met hetzelfde team. AIM Agency begrijpt echt wat MKB bedrijven nodig hebben.',
    rating: 5,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14a5ca983-1763300171126.png",
    alt: 'Middle-aged man with short brown hair in white shirt smiling in warehouse setting'
  },
  {
    id: 3,
    name: 'Lisa Vermeulen',
    role: 'Marketing Manager',
    company: 'CreativeHub Agency',
    content: 'Onze social media output is vertienvoudigd zonder extra personeel. De AI begrijpt onze merkidentiteit perfect en de content is altijd on-brand. Absolute game-changer voor ons bureau.',
    rating: 5,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1dba5a9ad-1772956588345.png",
    alt: 'Young woman with long dark hair in casual attire smiling in creative studio with colorful background'
  }];


  return (
    <section className={`py-16 lg:py-24 bg-gradient-to-b from-[#0a0a0a] to-background ${className}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Testimonials */}
        <div className="mb-16 lg:mb-24">
          <div className="text-center mb-8 lg:mb-12">
            <div className="inline-flex items-center space-x-2 glassmorphic px-4 py-2 rounded-full mb-6">
              <Icon name="ChatBubbleLeftRightIcon" size={16} className="text-primary" />
              <span className="text-sm font-medium text-foreground">Wat klanten zeggen</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground">
              Vertrouwd door{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
                toonaangevende bedrijven
              </span>
            </h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="glassmorphic rounded-3xl p-8 lg:p-12 shadow-premium">
              <div className="flex flex-col lg:flex-row items-center lg:items-start space-y-6 lg:space-y-0 lg:space-x-8">
                {/* Avatar */}
                <div className="flex-shrink-0">
                  <div className="relative w-24 h-24 lg:w-32 lg:h-32 rounded-full overflow-hidden ring-4 ring-primary/20">
                    <AppImage
                      src={testimonials[currentTestimonial].image}
                      alt={testimonials[currentTestimonial].alt}
                      className="w-full h-full object-cover" />

                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 text-center lg:text-left">
                  {/* Rating */}
                  <div className="flex justify-center lg:justify-start space-x-1 mb-4">
                    {[...Array(testimonials[currentTestimonial].rating)].map((_, i) =>
                    <Icon key={i} name="StarIcon" size={20} className="text-warning" variant="solid" />
                    )}
                  </div>

                  {/* Quote */}
                  <blockquote className="text-lg lg:text-xl text-foreground mb-6 leading-relaxed">
                    "{testimonials[currentTestimonial].content}"
                  </blockquote>

                  {/* Author */}
                  <div>
                    <div className="font-semibold text-foreground">
                      {testimonials[currentTestimonial].name}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {testimonials[currentTestimonial].role} bij {testimonials[currentTestimonial].company}
                    </div>
                  </div>
                </div>
              </div>

              {/* Navigation Dots */}
              <div className="flex justify-center space-x-2 mt-8">
                {testimonials.map((_, index) =>
                <button
                  key={index}
                  onClick={() => isHydrated && setCurrentTestimonial(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentTestimonial ?
                  'w-8 bg-primary' : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'}`
                  }
                  aria-label={`Go to testimonial ${index + 1}`}>
                </button>
                )}
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>);

};

export default SocialProof;